import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import cn from 'classnames';

import { CheckoutOrderFragment } from '@websky/graphql';
import Field from './Field/Field';
import { useTheme } from '../../../../../theme';
import { getOrder } from '../../../../../Checkout/store/order/selectors';
import InfoLinksLabel from '../../../../../InfoLinksLabel';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { BaseButton } from '../../../../../BaseComponents';

export interface FormStateProps {
	name: string;
	email: string;
	phone: string;
	comment: string;
}

export interface Props {
	className?: string;
	header?: string;
	description?: string;
	buttonText?: string;
	initialValues?: Partial<FormStateProps>;
	send: (params: any) => Promise<boolean>;
	isSending?: boolean;
	autoMode?: boolean;
	isRulesOptional?: boolean;
}

const CreateRequest: React.FC<Props> = ({
	className,
	header,
	description,
	buttonText,
	send,
	initialValues,
	isSending,
	autoMode,
	isRulesOptional
}) => {
	const theme = useTheme('Exchange').CreateRequest;
	const { t } = useTranslation('Exchange');

	const order = useSelector(getOrder);

	const onSubmitHandler = (values: any) => {
		send(values).then(data => {});
	};

	const formValues = { ...initialValues };

	// for correct validation of the phone number
	if (
		formValues.phone &&
		formValues.phone.startsWith('7') &&
		formValues.phone.replace(/\(|\)\s/, '').length === 11 &&
		parsePhoneNumberFromString(formValues.phone, 'RU')
	) {
		formValues.phone = `+${formValues.phone}`;
	}

	if (!formValues.phone.startsWith('+')) {
		formValues.phone = `+${formValues.phone}`;
	}

	return (
		<Form onSubmit={onSubmitHandler} initialValues={formValues}>
			{({ handleSubmit }) => (
				<form onSubmit={handleSubmit}>
					<div className={cn(theme.container, className)}>
						<div className={theme.header}>
							{header ?? t('Create request')}
							{description && <div className={theme.description}>{description}</div>}
						</div>

						<div className={cn(theme.row, theme.main)}>
							<Field name="name" type="text" label={t('Your name')} className={theme.field} />

							<Field name="email" type="email" label={t('Email')} className={theme.field} />

							<Field
								name="phone"
								type="phone"
								label={t('Your phone')}
								className={theme.field}
								displayInitialValueAsLocalNumber={false}
							/>
						</div>

						<div className={cn(theme.row)}>
							<Field
								name="comment"
								type="comment"
								label={t('Your comment')}
								className={cn(theme.field, theme.field_comment)}
								required={!autoMode}
							/>
						</div>
					</div>

					<div className={theme.controls}>
						{!isRulesOptional && (
							<Field
								name="acceptTerm"
								type="checkbox"
								label={
									<InfoLinksLabel
										className={theme.label}
										order={order as CheckoutOrderFragment}
										type={'exchange'}
									/>
								}
							/>
						)}

						<BaseButton
							variant="action"
							type="submit"
							className={theme.submit}
							isLoading={isSending}
							data-next-button={true}
						>
							{buttonText ?? t('Create request')}
						</BaseButton>
					</div>
				</form>
			)}
		</Form>
	);
};

export default CreateRequest;
